'use client';

import Box from '@mui/material/Box';
import { Stack, Typography } from '@mui/material';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { styled } from '@mui/material/styles';
import { useMemo } from 'react';
import { numberSeparator } from 'number-separator-js';
import { IconSvg } from '../Icon';
import { NumberFormat } from '../number-format';
import { PdpInfoDTO } from '@/types/models';
interface BorderLinearProgressProps {
  finished: boolean;
}
const BorderLinearProgress = styled(LinearProgress, {
  shouldForwardProp: prop => prop !== 'finished'
})<BorderLinearProgressProps>(({
  theme,
  finished
}) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: finished ? theme.palette.primaryBrand?.stroke4 : theme.palette.primaryBrand?.stroke4
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: finished ? theme.palette.primaryBrand?.text : theme.palette.primaryBrand?.text
  }
}));
interface InvestmentStatusProps {
  data: PdpInfoDTO;
}
const InvestmentStatus = ({
  data
}: InvestmentStatusProps): JSX.Element => {
  const texts = {
    title: 'سرمایه تأمین شده',
    desc1: 'حداقل سرمایه مورد نیاز اجرای طرح:',
    finish: 'تأمین شد',
    preValue: 'تومان',
    profitPerMonth: 'نحوه پرداخت سود',
    descriptionResult: `  هر ${data.profitPerMonth} ماه - ${data.percentPerMonth || 0}% `
  };
  const progressWidth = useMemo(() => {
    return +data.minInvestment / +data.maxInvestment;
  }, [+data.minInvestment, +data.maxInvestment]);
  const firstHalfAmount = useMemo(() => {
    return Math.min(+data.providedFund / +data.minInvestment * 100, 100);
  }, [+data.providedFund, +data.minInvestment]);
  const finished = data.status === 'funded';
  const color: {
    text: 'approve.text' | 'primaryBrand.text' | 'text.text';
    progressFinished: boolean;
  } = useMemo(() => {
    if (firstHalfAmount >= 100) {
      return {
        text: 'text.text',
        progressFinished: true
      };
    }
    return {
      text: 'text.text',
      progressFinished: false
    };
  }, [firstHalfAmount]);
  return <Box sx={{
    width: '100%',
    p: 'var(--spacing_m)'
    // backgroundColor: 'neutral.bg2',
  }} data-sentry-element="Box" data-sentry-component="InvestmentStatus" data-sentry-source-file="InvestmentStatus.tsx">
      <Box sx={{
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between'
    }} data-sentry-element="Box" data-sentry-source-file="InvestmentStatus.tsx">
        <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 'var(--spacing_xxs_nudge)'
      }} data-sentry-element="Box" data-sentry-source-file="InvestmentStatus.tsx">
          <Typography variant='captionSmallRegular__Wallfund' color={'text.text2'} data-sentry-element="Typography" data-sentry-source-file="InvestmentStatus.tsx">
            {texts.title}{' '}
          </Typography>

          <Typography color={color.text} variant='bodyLargeStrong__Wallfund' data-sentry-element="Typography" data-sentry-source-file="InvestmentStatus.tsx">
            {Math.floor(+data.providedFund / +data.maxInvestment * 100)}%
          </Typography>
        </Box>
        <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 'var(--spacing_xxs_nudge)'
      }} data-sentry-element="Box" data-sentry-source-file="InvestmentStatus.tsx">
          <Typography color={color.text} variant='bodyStrong__Wallfund' data-sentry-element="Typography" data-sentry-source-file="InvestmentStatus.tsx">
            {numberSeparator({
            text: (+data.providedFund > +data.maxInvestment ? +data.maxInvestment : +data.id === 15 ? 10_000_000_000 : data.providedFund).toString()
          })}
          </Typography>
          <Typography variant='captionLight__Wallfund' color={'text.text2'} data-sentry-element="Typography" data-sentry-source-file="InvestmentStatus.tsx">
            {texts.preValue}
          </Typography>
        </Box>
      </Box>
      <Stack sx={{
      alignItems: 'center',
      minHeight: 32
    }} direction={'row'} data-sentry-element="Stack" data-sentry-source-file="InvestmentStatus.tsx">
        <BorderLinearProgress sx={{
        width: `${progressWidth * 100}%`
      }} variant='determinate' value={firstHalfAmount} finished={finished} data-sentry-element="BorderLinearProgress" data-sentry-source-file="InvestmentStatus.tsx" />

        {color.progressFinished ? <IconSvg component='RocketFill' width='25' height='24' fill={finished ? '#007AFF' : '#007AFF'} /> : finished ? <IconSvg component='RocketOutlineFinish' width='25' height='24' fill={finished ? '#007AFF' : '#007AFF'} /> : <IconSvg component='RocketOutlineNotFinish' width='20' height='20' fill={finished ? '#007AFF' : '#007AFF'} />}

        <BorderLinearProgress sx={{
        width: `${(1 - progressWidth) * 100}%`
      }} variant='determinate' value={Math.floor((+data.providedFund - +data.minInvestment) / (+data.maxInvestment - +data.minInvestment) * 100)} finished={finished} data-sentry-element="BorderLinearProgress" data-sentry-source-file="InvestmentStatus.tsx" />
      </Stack>
      <Stack direction='row' justifyContent='space-between' data-sentry-element="Stack" data-sentry-source-file="InvestmentStatus.tsx">
        <Typography variant='subtitle3' data-sentry-element="Typography" data-sentry-source-file="InvestmentStatus.tsx">مبلغ هدف:</Typography>
        <Typography variant='subtitle3' data-sentry-element="Typography" data-sentry-source-file="InvestmentStatus.tsx">
          <NumberFormat value={+data.maxInvestment} displayType='text' thousandSeparator suffix=' تومان ' data-sentry-element="NumberFormat" data-sentry-source-file="InvestmentStatus.tsx" />
        </Typography>
      </Stack>
    </Box>;
};
export default InvestmentStatus;